// Module → tool names the agent may call while that module's cards are on screen.
// DERIVED from the backend card catalogue (api-service/app/services/llm/cards/catalogue.py),
// served on GET /api/v1/configurations/client → data.cards. No hand-kept mirror list here —
// edit the catalogue to change which tools a card exposes.
// Sent as `available_actions` with every agent.context_update for turn-time tool resolution.
import { useMemo } from 'react';
import { useGetClientConfigQuery, type CardDefinition } from '@/redux/internal-services/agentApi';

export type ModuleToolMap = Record<string, string[]>;

/** The `cards_version` this client is coded against. A higher server version is still readable
 *  (changes are additive) but is logged so the gap is noticed. */
export const SUPPORTED_CARDS_VERSION = 5;

/** The raw card catalogue (undefined until the client config loads). */
export function useCardCatalogue(): CardDefinition[] | undefined {
  const { data } = useGetClientConfigQuery();
  return data?.cards;
}

/** sam_cards.client_event_routing — whether SAM envelopes are routed on generic card events. */
export function useCardEventRouting(): boolean {
  const { data } = useGetClientConfigQuery();
  return !!data?.cards_event_routing;
}

/** sam_cards.context_fields — per card definition id, the row fields SAM is told about. */
export function useCardContextFields(): Record<string, string[]> {
  const { data } = useGetClientConfigQuery();
  return data?.cards_context_fields ?? {};
}

/** Catalogue plus load state — renderers show a spinner while loading and an error (never a
 *  silent fallback) when the client config request failed. */
export function useCardCatalogueState(): { cards: CardDefinition[] | undefined; isError: boolean } {
  const { data, isError } = useGetClientConfigQuery();
  return { cards: data?.cards, isError: isError && !data?.cards };
}

let warnedVersion = false;

/** Union of every enabled card's resolved tools, grouped by the module the card renders under. */
export function moduleToolsFromCards(cards: CardDefinition[] | undefined): ModuleToolMap {
  const out: Record<string, Set<string>> = {};
  for (const card of cards ?? []) {
    if (!card.enabled) continue;
    const bucket = (out[card.module] ??= new Set());
    card.tools.forEach(t => bucket.add(t));
  }
  return Object.fromEntries(Object.entries(out).map(([m, s]) => [m, [...s].sort()]));
}

/** Module → tools map from the catalogue. Empty until the client config loads — an empty
 *  `available_actions` means "no frontend restriction", so the backend scopes on its own. */
export function useModuleTools(): ModuleToolMap {
  const { data } = useGetClientConfigQuery();
  const version = data?.cards_version;
  if (version !== undefined && version > SUPPORTED_CARDS_VERSION && !warnedVersion) {
    warnedVersion = true;
    console.warn(`[cards] server cards_version ${version} > supported ${SUPPORTED_CARDS_VERSION}`);
  }
  return useMemo(() => moduleToolsFromCards(data?.cards), [data?.cards]);
}
